import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Card,
  CardContent,
  Stack,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Avatar,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  SmartToy as BotIcon,
  Flag as FlagIcon,
  FlagOutlined as FlagOutlinedIcon,
  AccessTime as TimeIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material';
import api from '../api';

function ConversationDetail() {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const [conversation, setConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [copySuccess, setCopySuccess] = useState('');

  useEffect(() => {
    fetchConversation();
  }, [conversationId]);

  const fetchConversation = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/wally-conversations/${conversationId}`);
      setConversation(response.data);
    } catch (err) {
      console.error('Error fetching conversation:', err);
      setError('Failed to load conversation details');
    } finally {
      setLoading(false);
    }
  };

  const toggleFlag = async () => {
    try {
      setUpdating(true);
      const newFlaggedStatus = !conversation.flagged;
      await api.patch(`/wally-conversations/${conversationId}/flag`, {
        flagged: newFlaggedStatus
      });
      setConversation(prev => ({
        ...prev,
        flagged: newFlaggedStatus
      }));
    } catch (err) {
      console.error('Error updating flag status:', err);
      setError('Failed to update flag status');
    } finally {
      setUpdating(false);
    }
  };

  const handleCopyToSlack = async () => {
    try {
      const messages = Array.isArray(conversation.messages) ? conversation.messages : [];
      const userName = conversation.userName || 'Unknown User';
      const userEmail = conversation.userEmail || 'No email';
      
      // Format conversation for Slack
      let slackText = `🤖 *Wally AI Conversation*\n`;
      slackText += `👤 *User:* ${userName} (${userEmail})\n`;
      slackText += `🆔 *User ID:* ${conversation.userId}\n`;
      slackText += `📅 *Date:* ${formatTimestamp(conversation.createdAt)}\n`;
      slackText += `📊 *Status:* ${conversation.status}\n`;
      if (conversation.flagged) {
        slackText += `🚩 *FLAGGED FOR REVIEW*\n`;
      }
      slackText += `💬 *Messages:* ${messages.length}\n\n`;
      
      slackText += `*Conversation:*\n`;
      slackText += `\`\`\`\n`;
      
      messages.forEach((message, index) => {
        const role = message.role === 'user' ? '👤 User' : '🤖 Wally';
        const timestamp = message.timestamp ? ` (${new Date(message.timestamp).toLocaleTimeString()})` : '';
        const decodedContent = decodeHtmlEntities(message.content);
        slackText += `${role}${timestamp}:\n${decodedContent}\n\n`;
      });
      
      slackText += `\`\`\`\n`;
      slackText += `🔗 *View Details:* ${window.location.href}`;
      
      // Copy to clipboard
      await navigator.clipboard.writeText(slackText);
      setCopySuccess('Conversation copied to clipboard! Ready to paste in Slack.');
      
      // Clear success message after 3 seconds
      setTimeout(() => setCopySuccess(''), 3000);
    } catch (err) {
      console.error('Failed to copy:', err);
      setCopySuccess('Failed to copy conversation');
      setTimeout(() => setCopySuccess(''), 3000);
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const decodeHtmlEntities = (text) => {
    if (!text) return text;
    
    // Create a temporary element to decode HTML entities
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'completed':
        return 'info';
      case 'flagged':
        return 'warning';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ mb: 2 }}
        >
          Back
        </Button>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!conversation) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ mb: 2 }}
        >
          Back
        </Button>
        <Alert severity="info">Conversation not found</Alert>
      </Box>
    );
  }

  const messages = Array.isArray(conversation.messages) ? conversation.messages : [];

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
          >
            Back
          </Button>
          <Typography variant="h4" component="h1">
            Conversation Details
          </Typography>
        </Box>
        
        <Box display="flex" alignItems="center" gap={1}>
          <Tooltip title="Copy to Slack">
            <IconButton
              onClick={handleCopyToSlack}
              color="primary"
            >
              <CopyIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={conversation.flagged ? "Remove flag" : "Flag conversation"}>
            <IconButton
              onClick={toggleFlag}
              disabled={updating}
              color={conversation.flagged ? "warning" : "default"}
            >
              {conversation.flagged ? <FlagIcon /> : <FlagOutlinedIcon />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Conversation Info */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack spacing={2}>
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <Typography variant="h6">
              Conversation ID: {conversation.id}
            </Typography>
            <Chip
              label={conversation.status}
              color={getStatusColor(conversation.status)}
              size="small"
            />
            {conversation.flagged && (
              <Chip
                icon={<FlagIcon />}
                label="Flagged"
                color="warning"
                size="small"
              />
            )}
          </Box>
          
          <Box display="flex" gap={4} flexWrap="wrap">
            <Box>
              <Typography variant="body2" color="text.secondary">
                User
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {conversation.userName || 'Unknown User'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {conversation.userEmail || 'No email'}
              </Typography>
            </Box>
            
            <Box>
              <Typography variant="body2" color="text.secondary">
                User ID
              </Typography>
              <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                {conversation.userId}
              </Typography>
            </Box>
            
            {conversation.threadId && (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Thread ID
                </Typography>
                <Typography variant="body1">
                  {conversation.threadId}
                </Typography>
              </Box>
            )}
            
            <Box>
              <Typography variant="body2" color="text.secondary">
                Created At
              </Typography>
              <Typography variant="body1">
                {formatTimestamp(conversation.createdAt)}
              </Typography>
            </Box>
            
            <Box>
              <Typography variant="body2" color="text.secondary">
                Updated At
              </Typography>
              <Typography variant="body1">
                {formatTimestamp(conversation.updatedAt)}
              </Typography>
            </Box>
          </Box>

          {conversation.summary && (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Summary
              </Typography>
              <Typography variant="body1">
                {conversation.summary}
              </Typography>
            </Box>
          )}
        </Stack>
      </Paper>

      {/* Messages */}
      <Typography variant="h5" gutterBottom>
        Messages ({messages.length})
      </Typography>
      
      {messages.length === 0 ? (
        <Alert severity="info">No messages found in this conversation.</Alert>
      ) : (
        <Stack spacing={2}>
          {messages.map((message, index) => (
            <Card
              key={index}
              sx={{
                backgroundColor: message.role === 'user' ? 'rgba(144, 202, 249, 0.05)' : 'rgba(244, 143, 177, 0.05)',
                border: '1px solid',
                borderColor: message.role === 'user' ? 'rgba(144, 202, 249, 0.2)' : 'rgba(244, 143, 177, 0.2)'
              }}
            >
              <CardContent>
                <Box display="flex" alignItems="flex-start" gap={2}>
                  <Avatar
                    sx={{
                      bgcolor: message.role === 'user' ? 'primary.main' : 'secondary.main',
                      width: 40,
                      height: 40
                    }}
                  >
                    {message.role === 'user' ? <PersonIcon /> : <BotIcon />}
                  </Avatar>
                  
                  <Box flex={1}>
                    <Box display="flex" alignItems="center" gap={2} mb={1}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {message.role === 'user' ? 'User' : 'Wally AI'}
                      </Typography>
                      {message.timestamp && (
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <TimeIcon fontSize="small" color="action" />
                          <Typography variant="caption" color="text.secondary">
                            {formatTimestamp(message.timestamp)}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    
                    <Typography
                      variant="body1"
                      sx={{
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word'
                      }}
                    >
                      {decodeHtmlEntities(message.content)}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      {/* Copy Success Message */}
      {copySuccess && (
        <Alert 
          severity="success" 
          sx={{ 
            position: 'fixed', 
            bottom: 20, 
            right: 20, 
            zIndex: 1000,
            minWidth: 300
          }}
        >
          {copySuccess}
        </Alert>
      )}
    </Box>
  );
}

export default ConversationDetail;
